import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { VacationRequestStatus } from '../../types';

interface VacationFiltersProps {
  statusFilter?: string;
  memberNameFilter: string;
  showMemberFilter: boolean;
  showStatusFilter?: boolean;
  onStatusChange?: (status: string) => void;
  onMemberNameChange: (name: string) => void;
  onClear: () => void;
}

const ALL_STATUSES = 'ALL';

export const VacationFilters: React.FC<VacationFiltersProps> = ({
  statusFilter = ALL_STATUSES,
  memberNameFilter,
  showMemberFilter,
  showStatusFilter = true,
  onStatusChange,
  onMemberNameChange,
  onClear,
}) => {
  const { t } = useTranslation('vacations');
  const hasFilters =
    (showStatusFilter && statusFilter !== ALL_STATUSES) || memberNameFilter.trim().length > 0;

  return (
    <div className="flex flex-wrap items-end gap-4">
      {showStatusFilter && (
        <div className="w-full sm:w-48">
          <Label className="mb-1.5 block text-sm">{t('filters.status.label')}</Label>
          <Select value={statusFilter} onValueChange={onStatusChange}>
            <SelectTrigger data-testid="vacation-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUSES}>{t('filters.status.all')}</SelectItem>
              {Object.values(VacationRequestStatus).map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`status.${status.toLowerCase()}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showMemberFilter && (
        <div className="w-full sm:w-64">
          <Label className="mb-1.5 block text-sm">{t('filters.memberName.label')}</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={memberNameFilter}
              onChange={(e) => onMemberNameChange(e.target.value)}
              placeholder={t('filters.memberName.placeholder')}
              className="flex h-10 w-full rounded-md border border-prosigliere-grey bg-pure-white pl-9 pr-3 py-2 text-sm ring-offset-background placeholder:text-almost-black/40 focus:outline-none focus:ring-2 focus:ring-prosigliere-blue focus:ring-offset-2"
              data-testid="vacation-filter-member-name"
            />
          </div>
        </div>
      )}

      {hasFilters && (
        <Button variant="ghost" size="sm" onClick={onClear} data-testid="vacation-filter-clear">
          <X className="h-4 w-4 mr-1" />
          {t('filters.clear')}
        </Button>
      )}
    </div>
  );
};
